import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useLazyQuery, useMutation, useQuery } from 'react-apollo';
import {
  GET_LISTAR_PRODUTOS_HOME,
  ALTERNAR_CONSULTORIA_FAVORITA,
  ALTERNAR_CURSO_FAVORITO,
  ALTERNAR_SEBRAETEC_FAVORITA,
  GET_INDICADORES_PRODUTOS_HOME,
  ARE_FAVORITOS,
  LISTAR_FORNECEDORES_RECENTES
} from './queries';
import HomeInfo from './components/HomeInfo';
import AffiliationTutorial from '../../components/AffiliationTutorial/AffiliationTutorial';
import SupplierCallout from './components/SupplierCallout';
import ProfessionalsHighlight from './components/ProfessionalsHighlight';
import SuggestionsForYou from './components/SuggestionsForYou';
import Hero from './components/Hero';
import CategoryTabs from './components/CategoryTabs';
import { ContextPerson } from '../../store/contexts/contextPerson';
import { isRefreshTokenUndefinedOrExpired } from '../../shared/util/utilToken';
import Config from '../../shared/Config';
import styles from './Home.module.css';

export default function Home() {
  const { pessoa } = useContext(ContextPerson);
  const [productsFavoritos, setProductsFavoritos] = useState([]);
  const [searchFavoritos, setSearchFavoritos] = useState(false);
  const [indicadoresProdutos, setIndicadoresProdutos] = useState({
    possuiConsultorias: undefined,
    possuiEventos: undefined,
    possuiSolucoesDigitais: undefined,
    possuiCursosOnline: undefined
  });

  const { loading: indicadoresProdutosLoading } = useQuery(
    GET_INDICADORES_PRODUTOS_HOME,
    {
      onCompleted(data) {
        if (data && data.getIndicadoresProdutosHome) {
          setIndicadoresProdutos({ ...data.getIndicadoresProdutosHome });
        }
      }
    }
  );

  const { loading: getListProductsLoading, data: produtosData } = useQuery(
    GET_LISTAR_PRODUTOS_HOME,
    {
      variables: { limit: 20 }
    }
  );

  const products = useMemo(
    () =>
      produtosData && produtosData.listarProdutosHome
        ? produtosData.listarProdutosHome.map(p => ({ ...p }))
        : [],
    [produtosData]
  );

  const [areFavoritos] = useLazyQuery(ARE_FAVORITOS, {
    onCompleted(data) {
      if (data && data.areFavoritos) {
        setProductsFavoritos(
          data.areFavoritos
            .filter(f => f.isFavorito)
            .map(
              f =>
                (f.consultoria && f.consultoria.id) ||
                (f.evento && f.evento.id) ||
                (f.curso && f.curso.id) ||
                (f.sebraetecFichaTecnica && f.sebraetecFichaTecnica.id)
            )
            .filter(Boolean)
        );
      }
      setSearchFavoritos(true);
    }
  });

  const entidadesFavoritaveis = useMemo(
    () =>
      products
        .map(p => {
          switch (p.tipo) {
            case 'CONSULTORIA':
              return p.id;
            case 'CURSO':
              return p.curso && p.curso.id;
            case 'SEBRAETEC_FICHA_TECNICA':
              return p.sebraetecFichaTecnica && p.sebraetecFichaTecnica.id;
            default:
              return null;
          }
        })
        .filter(Boolean),
    [products]
  );

  const [profissionais, setProfissionais] = useState([]);

  const [listarFornecedores] = useLazyQuery(LISTAR_FORNECEDORES_RECENTES, {
    onCompleted(data) {
      if (data && data.listarFornecedoresRecentes)
        setProfissionais(
          data.listarFornecedoresRecentes.map(item => ({
            ...item,
            profileLink: '/perfil/' + item.slug
          }))
        );
    }
  });

  const [alternarConsultoriaFavorita] = useMutation(
    ALTERNAR_CONSULTORIA_FAVORITA
  );

  const [alternarCursoFavorito] = useMutation(ALTERNAR_CURSO_FAVORITO);

  const [alternarSebraetecFavorita] = useMutation(ALTERNAR_SEBRAETEC_FAVORITA);

  const onToggleFavorite = product => {
    if (isRefreshTokenUndefinedOrExpired()) {
      const href = window.location.href;
      window.location.href = `${Config.loginUiUrl}/?requestedURL=${href}`;
      return;
    }
    switch (product.tipo) {
      case 'CONSULTORIA':
        alternarConsultoriaFavorita({
          variables: { consultoriaId: product.id }
        });
        break;
      case 'CURSO':
        alternarCursoFavorito({
          variables: { cursoId: product.curso && product.curso.id }
        });
        break;
      case 'SEBRAETEC_FICHA_TECNICA':
        alternarSebraetecFavorita({
          variables: {
            fichaTecnicaSebraetecId:
              product.sebraetecFichaTecnica && product.sebraetecFichaTecnica.id
          }
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    listarFornecedores({ variables: { limit: 10, apenasProdutos: false } });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      pessoa &&
      pessoa.id &&
      !searchFavoritos &&
      entidadesFavoritaveis.length
    ) {
      areFavoritos({
        variables: {
          entidadesId: entidadesFavoritaveis
        }
      });
    }
    // eslint-disable-next-line
  }, [pessoa, entidadesFavoritaveis]);

  return (
    <>
      {pessoa && pessoa.afiliacao && pessoa.afiliacao.mostrarTutorial && (
        <AffiliationTutorial />
      )}

      <div className={styles.containerHome}>
        <Hero />
        <CategoryTabs
          loading={indicadoresProdutosLoading}
          indicadoresProdutos={indicadoresProdutos}
        />

        <div className='ui-container'>
          <SuggestionsForYou
            products={products}
            loading={getListProductsLoading}
            productsFavoritos={productsFavoritos}
            onToggleFavorite={onToggleFavorite}
          />
        </div>

        <HomeInfo />

        <ProfessionalsHighlight profissionais={profissionais} />

        <SupplierCallout />
      </div>
    </>
  );
}
